import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/App";
import api, { errText } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1756483511246-12ae36997a1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBldGhuaWMlMjB3ZWFyJTIwZW1icm9pZGVyeSUyMHphcml8ZW58MHx8fHwxNzg0ODI3MjUxfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { password });
      login(data.token, data.user);
    } catch (err) {
      setError(errText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO} alt="Zari handwork" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading text-4xl font-semibold tracking-tight">Handcrafted elegance,<br />managed effortlessly.</h2>
          <p className="mt-3 text-white/70 max-w-md">Customers, orders, karigars, production and payments — your entire Zari business in one place.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="mb-10">
            <img src="/logo.png" alt="Anamta Noor" data-testid="login-logo" className="h-16 w-auto" />
            <p className="text-sm text-muted-foreground mt-2">Zari Handwork ERP</p>
          </div>

          <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Welcome back</h2>
          <p className="text-sm text-muted-foreground mb-8">Enter your password to sign in</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="h-11 rounded-lg"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-red-500">{error}</p>
            )}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-lg font-medium active:scale-95 transition-transform"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
